import Cookie from 'js-cookie';

type CookieAttributes = NonNullable<Parameters<typeof Cookie.set>[2]>;

const KEY = 'kn_anon_id';
const COOKIE_DAYS = 395;
const UUID_V4_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isValidUuidV4 = (s: string | null | undefined): s is string =>
    !!s && UUID_V4_RE.test(s);

const cookieDomain = (): string | undefined => {
    const host = window.location.hostname;
    const isKnitAndNote =
        host === 'knitandnote.com' || host.endsWith('.knitandnote.com');
    return isKnitAndNote ? '.knitandnote.com' : undefined;
};

const cookieOptions = (): CookieAttributes => {
    const opts: CookieAttributes = {
        expires: COOKIE_DAYS,
        sameSite: 'lax',
        path: '/',
        secure: window.location.protocol === 'https:',
    };
    const domain = cookieDomain();
    if (domain) opts.domain = domain;
    return opts;
};

const readCookie = (): string | null => {
    try {
        return Cookie.get(KEY) ?? null;
    } catch {
        return null;
    }
};

const writeCookie = (value: string): void => {
    try {
        Cookie.set(KEY, value, cookieOptions());
    } catch {
        // cookies blocked — fine, localStorage will carry it
    }
};

const readLocalStorage = (): string | null => {
    try {
        return window.localStorage.getItem(KEY);
    } catch {
        return null;
    }
};

const writeLocalStorage = (value: string): void => {
    try {
        window.localStorage.setItem(KEY, value);
    } catch {
        // localStorage blocked — nothing to do
    }
};

const generateUuid = (): string => {
    const c = window.crypto;
    if (typeof c.randomUUID === 'function') {
        return c.randomUUID();
    }
    // Fallback for older browsers — RFC4122 v4 from getRandomValues
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
        '',
    );
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Read-only accessor — returns existing anon_id from cookie or localStorage,
 * or null if neither has a valid UUIDv4. Never generates.
 *
 * Use this at signup time: per spec, we do not mint a fresh anon_id just to
 * attach to register, since it would link to no prior touches.
 */
export const getAnonId = (): string | null => {
    const fromCookie = readCookie();
    if (isValidUuidV4(fromCookie)) return fromCookie;
    const fromLs = readLocalStorage();
    if (isValidUuidV4(fromLs)) return fromLs;
    return null;
};

/**
 * Read or create the anon_id. Use on every landing — also refreshes the cookie
 * TTL so the 395-day window slides forward on each visit.
 *
 * Returns null only when both cookie and localStorage are unavailable (e.g.
 * Safari ITP with strict settings), in which case no attribution is possible.
 */
export const getOrCreateAnonId = (): string | null => {
    const existing = getAnonId();
    const value = existing ?? generateUuid();

    writeCookie(value);
    writeLocalStorage(value);

    // Confirm at least one store accepted the write — otherwise no attribution
    if (readCookie() !== value && readLocalStorage() !== value) {
        return null;
    }

    return value;
};
